import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';

// GraphQL Queries
const FETCH_BLOCKLIST = gql`
  query FetchBlocklist($sessionId: String!) {
    fetchBlocklist(sessionId: $sessionId) {
      success
      message
      count
      blocklist
    }
  }
`;

// GraphQL Mutations
const BLOCK_USER = gql`
  mutation BlockUser($sessionId: String!, $phoneNumber: String!) {
    blockUser(sessionId: $sessionId, phoneNumber: $phoneNumber) {
      success
      message
      phoneNumber
      action
    }
  }
`;

const UNBLOCK_USER = gql`
  mutation UnblockUser($sessionId: String!, $phoneNumber: String!) {
    unblockUser(sessionId: $sessionId, phoneNumber: $phoneNumber) {
      success
      message
      phoneNumber
      action
    }
  }
`;

const BLOCK_MULTIPLE_USERS = gql`
  mutation BlockMultipleUsers($sessionId: String!, $phoneNumbers: [String!]!) {
    blockMultipleUsers(sessionId: $sessionId, phoneNumbers: $phoneNumbers) {
      success
      message
      total
      blocked
      failed
      details {
        phoneNumber
        success
        error
      }
    }
  }
`;

const UNBLOCK_MULTIPLE_USERS = gql`
  mutation UnblockMultipleUsers($sessionId: String!, $phoneNumbers: [String!]!) {
    unblockMultipleUsers(sessionId: $sessionId, phoneNumbers: $phoneNumbers) {
      success
      message
      total
      unblocked
      failed
      details {
        phoneNumber
        success
        error
      }
    }
  }
`;

const BlockedUsers = () => {
  const [sessionId] = useState('session_1768652444410'); // Default session
  const [phoneNumberToBlock, setPhoneNumberToBlock] = useState('');
  const [bulkPhoneNumbers, setBulkPhoneNumbers] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // Fetch blocklist
  const { data, loading, error, refetch } = useQuery(FETCH_BLOCKLIST, {
    variables: { sessionId },
    fetchPolicy: 'network-only'
  });

  // Mutations
  const [blockUser] = useMutation(BLOCK_USER);
  const [unblockUser] = useMutation(UNBLOCK_USER);
  const [blockMultipleUsers] = useMutation(BLOCK_MULTIPLE_USERS);
  const [unblockMultipleUsers] = useMutation(UNBLOCK_MULTIPLE_USERS);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  const handleBlockUser = async () => {
    if (!phoneNumberToBlock.trim()) {
      showNotification('Please enter a phone number', 'error');
      return;
    }

    try {
      const result = await blockUser({
        variables: { sessionId, phoneNumber: phoneNumberToBlock }
      });

      if (result.data.blockUser.success) {
        showNotification(`✅ ${result.data.blockUser.message}`, 'success');
        setPhoneNumberToBlock('');
        refetch();
      } else {
        showNotification(`❌ ${result.data.blockUser.message}`, 'error');
      }
    } catch (err) {
      showNotification(`❌ Error: ${err.message}`, 'error');
    }
  };

  const handleUnblockUser = async (phoneNumber) => {
    try {
      const result = await unblockUser({
        variables: { sessionId, phoneNumber }
      });

      if (result.data.unblockUser.success) {
        showNotification(`✅ ${result.data.unblockUser.message}`, 'success');
        refetch();
      } else {
        showNotification(`❌ ${result.data.unblockUser.message}`, 'error');
      }
    } catch (err) {
      showNotification(`❌ Error: ${err.message}`, 'error');
    }
  };

  const handleBulkBlock = async () => {
    const numbers = bulkPhoneNumbers.split('\n').map(n => n.trim()).filter(n => n);

    if (numbers.length === 0) {
      showNotification('Please enter phone numbers (one per line)', 'error');
      return;
    }

    try {
      const result = await blockMultipleUsers({
        variables: { sessionId, phoneNumbers: numbers }
      });

      if (result.data.blockMultipleUsers.success) {
        showNotification(
          `✅ Blocked ${result.data.blockMultipleUsers.blocked} users, ${result.data.blockMultipleUsers.failed} failed`,
          'success'
        );
        setBulkPhoneNumbers('');
        refetch();
      } else {
        showNotification(`❌ ${result.data.blockMultipleUsers.message}`, 'error');
      }
    } catch (err) {
      showNotification(`❌ Error: ${err.message}`, 'error');
    }
  };

  const handleBulkUnblock = async () => {
    if (selectedUsers.length === 0) {
      showNotification('Please select users to unblock', 'error');
      return;
    }

    try {
      const result = await unblockMultipleUsers({
        variables: { sessionId, phoneNumbers: selectedUsers }
      });

      if (result.data.unblockMultipleUsers.success) {
        showNotification(
          `✅ Unblocked ${result.data.unblockMultipleUsers.unblocked} users, ${result.data.unblockMultipleUsers.failed} failed`,
          'success'
        );
        setSelectedUsers([]);
        refetch();
      } else {
        showNotification(`❌ ${result.data.unblockMultipleUsers.message}`, 'error');
      }
    } catch (err) {
      showNotification(`❌ Error: ${err.message}`, 'error');
    }
  };

  const toggleSelectUser = (jid) => {
    setSelectedUsers(prev =>
      prev.includes(jid) ? prev.filter(id => id !== jid) : [...prev, jid]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredBlocklist.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredBlocklist);
    }
  };

  const extractPhoneNumber = (jid) => {
    return jid.split('@')[0];
  };

  // Filter blocklist based on search
  const filteredBlocklist = data?.fetchBlocklist?.blocklist?.filter(jid =>
    extractPhoneNumber(jid).includes(searchTerm)
  ) || [];

  if (loading) {
    return (
      <div className="p-8 text-center py-32">
        <div className="w-12 h-12 border-4 border-bulky-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-xs font-normal text-bulky-navy-300 uppercase tracking-wider">Scanning Blocklist Matrix...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border-2 border-red-100 rounded-3xl p-8 flex items-center gap-6">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-red-500 shadow-sm">
            <span className="material-icons-round text-3xl">error_outline</span>
          </div>
          <div>
            <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">Access Restricted / Matrix Error</h2>
            <p className="text-red-600/70 text-sm font-normal mt-1">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-10 max-w-[1400px] mx-auto animate-in fade-in duration-700">
      {/* Notification Pipeline */}
      {notification.show && (
        <div className={`fixed top-4 md:top-6 left-1/2 -translate-x-1/2 md:left-auto md:right-6 z-[100] w-[90%] md:w-auto px-5 py-3 rounded-xl shadow-xl transition-all duration-500 animate-in slide-in-from-top-10 md:slide-in-from-right-full ${notification.type === 'success' ? 'bg-bulky-navy-800 text-white border-l-4 border-bulky-blue-500' : 'bg-red-600 text-white'
          } flex items-center gap-3 font-normal text-[9px] uppercase tracking-wider backdrop-blur-md`}>
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${notification.type === 'success' ? 'bg-bulky-blue-500/20' : 'bg-white/20'}`}>
            <span className="material-icons-round text-base">{notification.type === 'success' ? 'verified' : 'report'}</span>
          </div>
          <span className="flex-1">{notification.message}</span>
        </div>
      )}

      {/* Header Context */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6 overflow-hidden relative group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 relative z-10">
          <div>
            <div className="flex items-center gap-3 mb-1.5">
              <div className="w-1 h-5 bg-red-500 rounded-full"></div>
              <h2 className="text-lg md:text-xl font-medium text-bulky-navy-800 tracking-tight uppercase leading-none">Blocked Terminals</h2>
            </div>
            <p className="text-bulky-navy-400 text-[8px] md:text-[9px] font-normal uppercase tracking-wider md:tracking-[0.3em] ml-4 flex items-center gap-3">
              <span className="flex items-center gap-1.5 text-red-500">
                <span className="material-icons-round text-sm">security_update_warning</span>
                Restriction Matrix
              </span>
              <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
              Manage restricted access protocols and blacklisted identities
            </p>
          </div>
        </div>
      </div>

      {/* Stats Board */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-6">
        {[
          { label: 'Restrictions', value: data?.fetchBlocklist?.count || 0, icon: 'lock', color: 'bulky-navy-800', bg: 'bg-bulky-navy-50' },
          { label: 'Selected Clusters', value: selectedUsers.length, icon: 'checklist', color: 'bulky-blue-500', bg: 'bg-bulky-blue-50' },
          { label: 'Filtered Result', value: filteredBlocklist.length, icon: 'filter_list', color: 'emerald-500', bg: 'bg-emerald-50' }
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm relative overflow-hidden group hover:shadow-md transition-all duration-300">
            <div className={`absolute top-0 right-0 w-20 h-20 ${stat.bg} rounded-bl-full -mr-4 -mt-4 transition-all duration-500 group-hover:w-24 group-hover:h-24 opacity-30`}></div>
            <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center text-${stat.color} mb-6 shadow-sm group-hover:scale-105 transition-transform`}>
              <span className="material-icons-round text-xl">{stat.icon}</span>
            </div>
            <div className="text-3xl font-normal text-bulky-navy-800 mb-1 leading-none">{stat.value}</div>
            <div className="text-[8px] font-normal text-gray-400 uppercase tracking-wider leading-none opacity-60">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10 mb-12">
        {/* Input Matrix */}
        <div className="lg:col-span-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {/* Single Restriction */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm flex flex-col group relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500 shadow-inner group-hover:rotate-12 transition-transform">
                  <span className="material-icons-round text-xl">person_off</span>
                </div>
                <div>
                  <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">Individual Restriction</h2>
                  <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mt-1.5">Targeted access termination</p>
                </div>
              </div>
              <div className="relative group/input flex-1">
                <label className="block text-[9px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-0.5">Terminal Identifier</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within/input:text-red-500 transition-colors text-lg">phone_iphone</span>
                  <input
                    type="text"
                    placeholder="e.g., 919876543210"
                    value={phoneNumberToBlock}
                    onChange={(e) => setPhoneNumberToBlock(e.target.value)}
                    className="w-full pl-12 pr-5 py-3.5 bg-gray-50 border border-transparent rounded-xl focus:border-red-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 text-[11px] shadow-sm uppercase placeholder:text-gray-300"
                  />
                </div>
              </div>
              <button
                onClick={handleBlockUser}
                className="w-full mt-8 bg-red-600 hover:bg-red-700 text-white font-normal py-4 rounded-xl transition-all shadow-lg shadow-red-600/20 active:scale-95 text-[9px] uppercase tracking-wider flex items-center justify-center gap-3"
              >
                <span className="material-icons-round text-lg">enhanced_encryption</span>
                Authorize Restriction
              </button>
            </div>

            {/* Global/Bulk Restriction */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm flex flex-col group relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-bulky-navy-800"></div>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-10 h-10 rounded-xl bg-bulky-navy-50 flex items-center justify-center text-bulky-navy-800 shadow-inner group-hover:rotate-12 transition-transform">
                  <span className="material-icons-round text-xl">grid_view</span>
                </div>
                <div>
                  <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">Bulk Restriction Matrix</h2>
                  <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mt-1.5">Batch identity blacklisting</p>
                </div>
              </div>
              <div className="flex-1">
                <label className="block text-[9px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-0.5">Restriction Sequence</label>
                <textarea
                  placeholder="Enter multiple markers (one per line)..."
                  value={bulkPhoneNumbers}
                  onChange={(e) => setBulkPhoneNumbers(e.target.value)}
                  className="w-full px-5 py-4 bg-gray-50 border border-transparent rounded-xl focus:border-bulky-navy-800 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 min-h-[140px] text-[11px] resize-none shadow-sm uppercase placeholder:text-gray-300"
                />
              </div>
              <button
                onClick={handleBulkBlock}
                className="w-full mt-8 bg-bulky-navy-800 hover:bg-bulky-navy-900 text-white font-normal py-4 rounded-xl transition-all shadow-lg shadow-black/10 active:scale-95 text-[9px] uppercase tracking-wider flex items-center justify-center gap-3"
              >
                <span className="material-icons-round text-lg">shield</span>
                Execute Bulk sequence
              </button>
            </div>
          </div>
        </div>

        {/* List Matrix */}
        <div className="lg:col-span-12">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Extended Controls */}
            <div className="p-5 border-b border-gray-50 flex flex-col sm:flex-row justify-between items-center gap-6 bg-gray-50/30">
              <div className="relative w-full sm:flex-1 group/search">
                <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within/search:text-bulky-blue-500 transition-colors text-lg">manage_search</span>
                <input
                  type="text"
                  placeholder="Search restricted clusters..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-6 py-3 bg-white border border-transparent rounded-xl focus:border-bulky-blue-500 focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm text-[10px] uppercase"
                />
              </div>

              {selectedUsers.length > 0 && (
                <button
                  onClick={handleBulkUnblock}
                  className="w-full sm:w-auto px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-normal rounded-lg shadow-lg shadow-emerald-500/10 active:scale-95 transition-all flex items-center justify-center gap-2 text-[9px] uppercase tracking-wider"
                >
                  <span className="material-icons-round text-base">verified_user</span>
                  Restore access ({selectedUsers.length})
                </button>
              )}
            </div>

            {/* Selection Header */}
            {filteredBlocklist.length > 0 && (
              <div className="px-6 py-4 bg-bulky-navy-50/50 flex items-center border-b border-gray-100/50">
                <label className="flex items-center gap-4 cursor-pointer group">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      id="select-all"
                      checked={selectedUsers.length === filteredBlocklist.length}
                      onChange={toggleSelectAll}
                      className="hidden"
                    />
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${selectedUsers.length === filteredBlocklist.length ? 'bg-bulky-blue-500 border-bulky-blue-500' : 'bg-white border-gray-300'}`}>
                      {selectedUsers.length === filteredBlocklist.length && <span className="material-icons-round text-white text-sm">done</span>}
                    </div>
                  </div>
                  <span className="text-[9px] font-normal text-bulky-navy-500 uppercase tracking-wider group-hover:text-bulky-navy-800 transition-colors">Master Selection Toggle</span>
                </label>
              </div>
            )}

            {/* List Pipeline */}
            {filteredBlocklist.length === 0 ? (
              <div className="p-20 text-center bg-gray-50/10">
                <div className="w-20 h-20 bg-white shadow-lg rounded-2xl flex items-center justify-center mx-auto mb-8 text-gray-200 border border-gray-50 group-hover:scale-105 transition-transform duration-500">
                  <span className="material-icons-round text-5xl">verified</span>
                </div>
                <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">No restrictions active</h2>
                <p className="text-gray-400 text-[9px] font-normal uppercase tracking-[0.3em] mt-2 max-w-sm mx-auto opacity-60 uppercase">The current communication session is optimized and free of identity blacklisting.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-6 bg-gray-50/40">
                {filteredBlocklist.map((jid) => {
                  const isSelected = selectedUsers.includes(jid);
                  return (
                    <div
                      key={jid}
                      className={`p-5 bg-white rounded-2xl border transition-all duration-300 flex flex-col group relative overflow-hidden h-full ${isSelected ? 'border-bulky-blue-500 shadow-md' : 'border-gray-100 hover:border-bulky-blue-200 hover:shadow-sm'}`}
                    >
                      <div className="absolute top-0 right-0 w-12 h-12 bg-bulky-blue-500/5 rounded-full -mr-6 -mt-6"></div>

                      <div className="flex items-start justify-between mb-6 relative z-10">
                        <label className="relative flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectUser(jid)}
                            className="hidden"
                          />
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-bulky-blue-500 border-bulky-blue-500' : 'bg-gray-50 border-gray-200'}`}>
                            {isSelected && <span className="material-icons-round text-white text-sm">done</span>}
                          </div>
                        </label>
                        <button
                          onClick={() => handleUnblockUser(jid)}
                          className={`w-10 h-10 rounded-xl transition-all active:scale-95 flex items-center justify-center shadow-sm ${isSelected ? 'bg-emerald-500 text-white' : 'bg-gray-50 text-gray-400 hover:bg-emerald-500 hover:text-white'}`}
                        >
                          <span className="material-icons-round text-lg">verified_user</span>
                        </button>
                      </div>

                      <div className="relative z-10">
                        <div className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-2 leading-none opacity-60">Identity Marker</div>
                        <div className="font-normal text-bulky-navy-800 tracking-tight text-base mb-1.5 truncate">
                          {extractPhoneNumber(jid)}
                        </div>
                        <div className="text-[9px] font-normal text-bulky-blue-600 uppercase tracking-tighter bg-bulky-blue-50 inline-block px-2.5 py-1 rounded border border-bulky-blue-100/50">
                          {jid}
                        </div>
                      </div>

                      <div className={`absolute bottom-0 left-0 w-full h-1 transition-all duration-500 ${isSelected ? 'bg-bulky-blue-500' : 'bg-transparent group-hover:bg-bulky-blue-200'}`}></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockedUsers;
